/** A local calendar day as `YYYY-MM-DD`. String order is date order. */
export type DayKey = string;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const FULL_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_MS = 86_400_000;

export function parseDay(day: DayKey): Date {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function toDayKey(date: Date): DayKey {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/** Timestamps are stored as midnight Central, so the local day is the intended day. */
export function timestampToDay(value: string | null | undefined): DayKey | null {
  if (!value) return null;
  const date = new Date(value);
  // Some legacy dates don't parse (e.g. "2025-4-15T05:00:00.000Z"); a NaN day key never ends a day loop
  return Number.isNaN(date.getTime()) ? null : toDayKey(date);
}

/** Local midnight of the day, as a query Timestamp. */
export function timestampFromDay(day: DayKey): string {
  return parseDay(day).toISOString();
}

export function addDays(day: DayKey, n: number): DayKey {
  const date = parseDay(day);
  date.setDate(date.getDate() + n);
  return toDayKey(date);
}

export function diffDays(from: DayKey, to: DayKey): number {
  return Math.round((parseDay(to).getTime() - parseDay(from).getTime()) / DAY_MS);
}

export function mondayOf(day: DayKey): DayKey {
  return addDays(day, -((parseDay(day).getDay() + 6) % 7));
}

export function isWeekend(day: DayKey): boolean {
  const weekday = parseDay(day).getDay();
  return weekday === 0 || weekday === 6;
}

export function minDay(a: DayKey, b: DayKey): DayKey {
  return a < b ? a : b;
}

export function maxDay(a: DayKey, b: DayKey): DayKey {
  return a > b ? a : b;
}

/** "Nov 3" */
export function formatDay(day: DayKey): string {
  const date = parseDay(day);
  return `${MONTHS[date.getMonth()]} ${date.getDate()}`;
}

/** "Mon, Nov 3" */
export function formatDayLong(day: DayKey): string {
  const date = parseDay(day);
  return `${WEEKDAYS[date.getDay()]}, ${MONTHS[date.getMonth()]} ${date.getDate()}`;
}

export function weekdayShort(day: DayKey): string {
  return WEEKDAYS[parseDay(day).getDay()];
}

/** "November 2026" */
export function formatMonth(month: DayKey): string {
  const date = parseDay(month);
  return `${FULL_MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

/** "Oct 26 – Nov 8, 2026", "Nov 2 – 15, 2026" or "Dec 28, 2026 – Jan 10, 2027" */
export function formatSpan(start: DayKey, end: DayKey): string {
  const a = parseDay(start);
  const b = parseDay(end);
  if (a.getFullYear() !== b.getFullYear())
    return `${formatDay(start)}, ${a.getFullYear()} – ${formatDay(end)}, ${b.getFullYear()}`;
  return `${formatDay(start)} – ${a.getMonth() === b.getMonth() ? b.getDate() : formatDay(end)}, ${b.getFullYear()}`;
}

export function monthStart(day: DayKey): DayKey {
  return `${day.slice(0, 8)}01`;
}

export function addMonths(month: DayKey, n: number): DayKey {
  const date = parseDay(monthStart(month));
  date.setMonth(date.getMonth() + n);
  return toDayKey(date);
}

export function monthEnd(month: DayKey): DayKey {
  return addDays(addMonths(month, 1), -1);
}
